<script setup>
// A conversa da pessoa, dentro da própria tela de histórico.
//
// É o mesmo ConversationBox da caixa de entrada, não uma releitura das
// mensagens: cabeçalho, thread e caixa de resposta reais, com o mesmo estado
// do Vuex. O preço é que este componente precisa selecionar a conversa no
// store como a rota de conversas faz — carregar (`getConversation`) e só então
// ativar (`setActiveChat`) — e limpar a seleção ao sair, senão o operador
// volta para a caixa de entrada com esta conversa presa como ativa.
import { computed, onBeforeUnmount, onErrorCaptured, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  title: { type: String, default: '' },
});

defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const loading = ref(false);
// A caixa de conversa arrasta meia caixa de entrada consigo. Se algo lá dentro
// estourar, o histórico não pode cair junto: o painel vira um link para a
// conversa na caixa de entrada e a tela continua de pé.
const crashed = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const inboxLink = computed(() =>
  frontendURL(conversationUrl({ accountId, id: props.conversationId }))
);

function findChat(id) {
  return store.getters.getAllConversations.find(chat => chat.id === Number(id));
}

async function open(id) {
  if (!id) return;
  loading.value = true;
  crashed.value = false;
  try {
    if (!findChat(id)) {
      await store.dispatch('getConversation', id);
    }
    const chat = findChat(id);
    if (chat && chat.id !== currentChat.value?.id) {
      await store.dispatch('setActiveChat', { data: chat });
    }
    // O store guarda a conversa em cache e `UPDATE_CONVERSATION` descarta o
    // array de mensagens ao atualizar uma conversa que já existe — então
    // reabrir o painel mostrava a thread de quando ela foi carregada pela
    // primeira vez. Este sync busca o que chegou depois e mescla.
    if (chat) {
      await store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(id),
      });
    }
  } catch (error) {
    crashed.value = true;
  } finally {
    loading.value = false;
  }
}

onErrorCaptured(() => {
  crashed.value = true;
  return false;
});

watch(() => props.conversationId, open, { immediate: true });

onBeforeUnmount(() => store.dispatch('clearSelectedState'));
</script>

<template>
  <div
    class="flex flex-col overflow-hidden border rounded-lg border-n-weak bg-n-background h-[70vh] min-h-[420px]"
  >
    <div
      class="flex items-center justify-between gap-2 px-3 py-2 border-b border-n-weak"
    >
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ title || t('OUTBOUNDS.HISTORY.CONVERSATION.TITLE') }}
      </span>
      <span class="flex items-center gap-3 shrink-0">
        <a
          :href="inboxLink"
          target="_blank"
          rel="noopener"
          class="text-xs text-n-slate-11 hover:text-n-slate-12"
        >
          {{ t('OUTBOUNDS.HISTORY.CONVERSATION.OPEN_INBOX') }}
        </a>
        <button
          type="button"
          class="text-n-slate-11 hover:text-n-slate-12"
          :aria-label="t('OUTBOUNDS.HISTORY.CONVERSATION.REFRESH')"
          :disabled="loading"
          @click="open(conversationId)"
        >
          <i class="i-lucide-refresh-cw size-4" />
        </button>
        <button
          type="button"
          class="text-n-slate-11 hover:text-n-slate-12"
          :aria-label="t('OUTBOUNDS.HISTORY.CONVERSATION.CLOSE')"
          @click="$emit('close')"
        >
          <i class="i-lucide-x size-4" />
        </button>
      </span>
    </div>

    <p v-if="loading" class="p-4 text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.CONVERSATION.LOADING') }}
    </p>

    <div v-else-if="crashed" class="p-4 text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.CONVERSATION.FAILED') }}
      <a :href="inboxLink" target="_blank" rel="noopener" class="text-n-brand">
        {{ t('OUTBOUNDS.HISTORY.CONVERSATION.OPEN_INBOX') }}
      </a>
    </div>

    <div v-else-if="currentChat?.id" class="flex flex-1 min-h-0">
      <ConversationBox
        :is-inbox-view="false"
        :is-contact-panel-open="false"
        :is-on-expanded-layout="false"
      />
    </div>

    <p v-else class="p-4 text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.CONVERSATION.NOT_FOUND') }}
    </p>
  </div>
</template>
